import React, {useEffect, useState} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import {useAccountStyle} from '@containers/accountStyle/hooks/useAccountStyle'

interface IProps {
	timeZone?: string
}

// Time parts in the zone; an unknown zone falls back to the browser time.
const partsInZone = (date: Date, timeZone?: string) => {
	const read = (zone?: string) => {
		const parts = new Intl.DateTimeFormat('uk-UA', {
			timeZone: zone,
			hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
			day: '2-digit', month: 'long', weekday: 'long',
		}).formatToParts(date)
		const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
		return {
			hours: Number(get('hour')),
			minutes: Number(get('minute')),
			seconds: Number(get('second')),
			date: `${get('weekday')}, ${get('day')} ${get('month')}`,
		}
	}
	try {
		return read(timeZone)
	} catch {
		return read()
	}
}

const Hand = ({angle, length, width, color}: {angle: number, length: number, width: number, color: string}) => (
	<line
		x1={50} y1={50} x2={50} y2={50 - length}
		stroke={color} strokeWidth={width} strokeLinecap={'round'}
		transform={`rotate(${angle} 50 50)`}
	/>
)

const ZoneClock = ({timeZone}: IProps) => {
	const {tokens} = useAccountStyle()
	const [now, setNow] = useState(() => new Date())

	useEffect(() => {
		const timer = setInterval(() => setNow(new Date()), 1000)
		return () => clearInterval(timer)
	}, [])

	const {hours, minutes, seconds, date} = partsInZone(now, timeZone)
	const pad = (value: number) => String(value).padStart(2, '0')

	return (
		<Stack direction={'row'} alignItems={'center'} gap={2}>
			<Box component={'svg'} viewBox={'0 0 100 100'} sx={{width: 76, height: 76, flexShrink: 0, color: 'text.primary'}}>
				<circle cx={50} cy={50} r={47} fill={'none'} stroke={tokens.accent} strokeWidth={3}/>
				{Array.from({length: 12}, (_, index) => (
					<line
						key={index}
						x1={50} y1={7} x2={50} y2={index % 3 === 0 ? 15 : 11}
						stroke={'currentColor'} strokeOpacity={index % 3 === 0 ? .8 : .35} strokeWidth={index % 3 === 0 ? 3 : 2}
						strokeLinecap={'round'}
						transform={`rotate(${index * 30} 50 50)`}
					/>
				))}
				<Hand angle={(hours % 12) * 30 + minutes / 2} length={24} width={5} color={'currentColor'}/>
				<Hand angle={minutes * 6 + seconds / 10} length={34} width={3.5} color={'currentColor'}/>
				<Box component={'g'} sx={{transition: seconds === 0 ? 'none' : 'transform .3s cubic-bezier(.4, 2.3, .6, 1)', transformOrigin: '50px 50px', transform: `rotate(${seconds * 6}deg)`}}>
					<line x1={50} y1={58} x2={50} y2={10} stroke={tokens.accent} strokeWidth={1.5} strokeLinecap={'round'}/>
				</Box>
				<circle cx={50} cy={50} r={3.5} fill={tokens.accent}/>
			</Box>
			<Box>
				<Typography variant={'h5'} fontWeight={600} sx={{fontVariantNumeric: 'tabular-nums', lineHeight: 1.1}}>
					{pad(hours)}:{pad(minutes)}
					<Typography component={'span'} variant={'body1'} color={'text.secondary'} sx={{fontVariantNumeric: 'tabular-nums'}}>
						:{pad(seconds)}
					</Typography>
				</Typography>
				<Typography variant={'body2'} color={'text.secondary'}>{date}</Typography>
				{timeZone && <Typography variant={'caption'} color={'text.secondary'}>{timeZone}</Typography>}
			</Box>
		</Stack>
	)
}

export default ZoneClock
